<template>
  <div class="unified-login-page">
    <div class="top-actions">
      <button
        v-if="canCloseApp"
        @click="closeApplication"
        class="close-app-btn"
        title="Close application"
      >
        Close App
      </button>
      <button @click="toggleTheme" class="theme-toggle-btn" :title="currentTheme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'">
        <font-awesome-icon v-if="currentTheme === 'dark'" icon="fa-solid fa-sun"></font-awesome-icon>
        <font-awesome-icon v-else icon="fa-solid fa-moon"></font-awesome-icon>
      </button>
    </div>

    <div class="banner">
      <div class="container d-flex justify-content-center">
        <div class="logo-container">
          <div class="lightning-logo">L⚡tning POS</div>
        </div>
      </div>
    </div>

    <div class="container d-flex justify-content-center">
      <div class="login-container">
        <!-- Loading Overlay -->
        <transition name="fade">
          <div v-if="showLoader" class="loader-overlay">
            <div class="loader-content">
              <div class="spinner"></div>
              <div class="loader-message">{{ loaderMessage }}</div>
            </div>
          </div>
        </transition>

        <!-- Step only after resolveLoginStep — never paint franchise while bound store still exists (cashier logout). -->
        <transition name="slide-fade" mode="out-in">
          <component
            v-if="currentStep && currentStepComponent"
            :is="currentStepComponent"
            :key="currentStep"
            @next="handleNext"
            @error="handleError"
          />
        </transition>
      </div>
    </div>

    <transition name="fade">
      <div v-if="showUpdateGate" class="update-gate" role="alertdialog" aria-modal="true" aria-labelledby="update-gate-title" aria-describedby="update-gate-detail">
        <div class="update-gate-card">
          <div class="update-gate-icon" aria-hidden="true">
            <font-awesome-icon icon="fa-solid fa-circle-exclamation" />
          </div>
          <h1 class="update-gate-title" id="update-gate-title">This till can't be used until it's updated</h1>
          <p class="update-gate-detail" id="update-gate-detail">
            You are on <strong>v{{ deviceUpdate.currentVersion }}</strong> — store latest is
            <strong>v{{ deviceUpdate.latestVersion }}</strong>. Support can run the manual update for you.
          </p>
          <p class="update-gate-fineprint">
            The till is locked until the app is updated. Request support to come and update it, or ask them to confirm the update is complete and tap "Check again".
          </p>
          <div class="update-gate-actions">
            <button type="button" class="btn btn-primary" @click="openRequestUpdate" :disabled="updateRequestSent">
              {{ updateRequestSent ? 'Request sent' : 'Request update' }}
            </button>
            <button type="button" class="btn btn-link check-again-btn" @click="gateRetry" :disabled="checking">
              <font-awesome-icon v-if="checking" icon="fa-solid fa-spinner" spin />
              <span v-else>Check again</span>
            </button>
          </div>
          <p v-if="updateRequestSent" class="update-gate-sent" role="status">
            Request sent — support will contact you. The till will unlock automatically once the app is updated.
          </p>
        </div>
      </div>
    </transition>

    <div class="footer-links">
      <p class="text-center">Powered By <a target="_blank" href="https://chrilantech.com" class="branded-link">Chrilan Technology</a></p>
      <p class="text-center"><a target="_blank" href="https://wa.me/260958168829" class="support-link">Contact Support</a></p>
      <p v-if="appVersion" class="text-center version-label">Version {{ appVersion }}</p>
      <p v-if="deviceId" class="text-center version-label device-id-label" :title="deviceId">Device ID: {{ deviceId }}</p>
      <p v-if="deviceName" class="text-center version-label device-id-label" :title="deviceName">Device Name: {{ deviceName }}</p>
      <p v-if="deviceSerial" class="text-center version-label device-id-label" :title="deviceSerial">Serial: {{ deviceSerial }}</p>
    </div>

    <RequestUpdateModal
      :visible="showRequestModal"
      :store-db="modalStoreDB"
      :store-name="modalStoreName"
      :device-name="deviceName"
      :app-name="localAppName"
      :current-version="deviceUpdate ? deviceUpdate.currentVersion : ''"
      :latest-version="deviceUpdate ? deviceUpdate.latestVersion : ''"
      @close="closeRequestModal"
      @submitted="onRequestSubmitted"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import FranchiseLoginStep from '@/components/login/steps/FranchiseLoginStep.vue';
import StoreSelectionStep from '@/components/login/steps/StoreSelectionStep.vue';
import PinLoginStep from '@/components/login/steps/PinLoginStep.vue';
import UserSelectionStep from '@/components/login/steps/UserSelectionStep.vue';
import OtpVerificationStep from '@/components/login/steps/OtpVerificationStep.vue';
import FloatAssignmentStep from '@/components/login/steps/FloatAssignmentStep.vue';
import { Device } from '@/services/Device';
import appPkg from '../../package.json';
import { resolveLoginStep, hasFranchiseCredentials } from '@/utils/resolveLoginStep';
import { checkDeviceVersion, getLocalAppName } from '@/services/deviceVersionService';
import RequestUpdateModal from '@/components/login/RequestUpdateModal.vue';

export default {
  name: 'UnifiedLoginView',
  components: {
    FranchiseLoginStep,
    StoreSelectionStep,
    PinLoginStep,
    UserSelectionStep,
    OtpVerificationStep,
    FloatAssignmentStep,
    RequestUpdateModal
  },
  data() {
    // No step until resolveLoginStep runs — avoids franchise flash on cashier logout
    // when default_store is still bound in Vuex/disk.
    return {
      currentStep: null,
      showLoader: true,
      loaderMessage: 'Loading...',
      stepData: {},
      canCloseApp: false,
      deviceId: '',
      deviceName: '',
      deviceSerial: '',
      deviceUpdate: null,
      updateRequestSent: false,
      checking: false,
      showRequestModal: false
    };
  },
  computed: {
    ...mapGetters(['getCurrentTheme']),
    currentTheme() {
      return this.getCurrentTheme;
    },
    appVersion() {
      return appPkg && appPkg.version ? appPkg.version : '';
    },
    localAppName() {
      return getLocalAppName();
    },
    showUpdateGate() {
      return Boolean(
        this.deviceUpdate
        && this.deviceUpdate.needsUpdate
        && this.deviceName
      );
    },
    modalStoreDB() {
      const ds = this.$store.getters.getDefaultStore;
      // Hedge key spellings seen across commit paths (deep-link auto-login,
      // subscription-API list, Storage hydration). Mirrors the key fallback
      // in OnlineOrdersView.vue:544.
      return (ds && (ds.StoreDB || ds.storeDB || ds.database || ds.db)) || '';
    },
    modalStoreName() {
      const ds = this.$store.getters.getDefaultStore;
      return (ds && (ds.StoreName || ds.storename || ds.storeName)) || '';
    },
    currentStepComponent() {
      const stepMap = {
        franchise: 'FranchiseLoginStep',
        store: 'StoreSelectionStep',
        pin: 'PinLoginStep',
        user: 'UserSelectionStep',
        otp: 'OtpVerificationStep',
        float: 'FloatAssignmentStep'
      };
      return this.currentStep ? (stepMap[this.currentStep] || null) : null;
    }
  },
  created() {
    // Same-session logout: Vuex still has the bound store — never open franchise.
    const defaultStore = this.$store.getters.getDefaultStore;
    if (defaultStore && defaultStore.StoreDB) {
      this.loaderMessage = 'Validating subscription...';
    }
  },
  async mounted() {
    this.canCloseApp = typeof window !== 'undefined' && typeof window.electron?.closeApp === 'function';
    this.initializeTheme();
    try {
      const info = await Device.getLoginDeviceInfo();
      this.deviceId = info.deviceId || '';
      this.deviceName = info.deviceName || '';
      this.deviceSerial = info.deviceSerial || '';
    } catch (e) {
      this.deviceId = '';
      this.deviceName = '';
      this.deviceSerial = '';
    }
    await this.determineInitialStep();
    // Fire-and-forget device check-in. Never blocks login.
    this.runDeviceCheckIn();
  },
  methods: {
    ...mapActions(['toggleTheme', 'initializeTheme']),

    /**
     * Resume login after launch / cashier logout / store leave.
     * Step comes from resolveLoginStep(auth footprint), never a franchise default.
     */
    async determineInitialStep() {
      this.showLoader = true;
      if (!this.loaderMessage) {
        this.loaderMessage = 'Loading...';
      }

      const { Storage } = await import('@/services/Storage');
      const { getValue } = await import('@/services/storageAdapter');

      let defaultStore = this.$store.getters.getDefaultStore;
      if (!(defaultStore && defaultStore.StoreDB)) {
        try {
          const persisted = await getValue('default_store');
          if (persisted && persisted.StoreDB) {
            defaultStore = persisted;
            this.$store.commit('setDefaultStore', persisted);
          }
        } catch (_) { /* ignore */ }
      }

      let admin = null;
      try {
        admin = await Storage.getAdminUser();
      } catch (_) {
        admin = null;
      }

      const step = resolveLoginStep(defaultStore, hasFranchiseCredentials(admin));

      if (step === 'pin') {
        await this.validateSubscription();
        return;
      }

      this.currentStep = step;
      this.showLoader = false;
    },

    async validateSubscription() {
      this.showLoader = true;
      this.loaderMessage = 'Validating subscription...';

      try {
        const isExpired = await this.$store.dispatch('checkExpiry');
        if (isExpired === true) {
          this.$router.push({ name: 'system-expired' });
          return;
        }
        this.currentStep = 'pin';
        this.showLoader = false;
      } catch (e) {
        this.showLoader = false;
        this.handleError('Unable to validate subscription. Please try again.');
        this.currentStep = 'store';
      }
    },

    async handleNext({ step, data }) {
      this.stepData = { ...this.stepData, ...data };

      switch (step) {
        case 'franchise-complete':
          await this.handleFranchiseComplete();
          break;
        case 'store-complete':
          await this.handleStoreComplete();
          break;
        case 'pin-complete':
          await this.handlePinComplete(data);
          break;
        case 'user-complete':
          await this.handleUserComplete(data);
          break;
        case 'otp-complete':
          await this.handleOtpComplete();
          break;
        case 'float-complete':
          await this.handleFloatComplete();
          break;
      }
    },

    async handleFranchiseComplete() {
      this.showLoader = true;
      this.loaderMessage = 'Logging into franchise...';
      
      // Small delay for smooth transition
      await new Promise(resolve => setTimeout(resolve, 500));
      
      this.showLoader = false;
      this.currentStep = 'store';
    },

    async handleStoreComplete() {
      this.showLoader = true;
      this.loaderMessage = 'Loading store info...';

      await new Promise(resolve => setTimeout(resolve, 500));

      // Validate subscription after store selection
      await this.validateSubscription();

      // Reset banner state — a new store means a fresh check-in
      this.deviceUpdate = null;
      this.bannerDismissed = false;
      this.runDeviceCheckIn();
    },

    async handlePinComplete(data) {
      const { isAdmin, username } = data;
      console.log('[UnifiedLoginView] handlePinComplete', { isAdmin, username });

      this.showLoader = true;
      this.loaderMessage = 'Authenticating user...';

      await new Promise(resolve => setTimeout(resolve, 500));

      this.showLoader = false;

      if (isAdmin) {
        // Admin users need to select a support user
        console.log('[UnifiedLoginView] isAdmin=true → routing to support staff picker (step=user)');
        this.currentStep = 'user';
      } else {
        // Regular users go to float
        console.log('[UnifiedLoginView] isAdmin=false → skipping support staff picker, routing to float/POS', {
          isOnline: navigator.onLine,
        });
        if (navigator.onLine) {
          this.currentStep = 'float';
        } else {
          // Offline: skip float and go directly to POS
          this.$router.push({ name: 'pos' });
        }
      }
    },

    async handleUserComplete(data) {
      this.showLoader = true;
      this.loaderMessage = 'Preparing user selection...';
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      this.showLoader = false;
      this.currentStep = 'otp';
    },

    async handleOtpComplete() {
      this.showLoader = true;
      this.loaderMessage = 'Verifying OTP...';
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      this.showLoader = false;
      
      if (navigator.onLine) {
        this.currentStep = 'float';
      } else {
        this.$router.push({ name: 'pos' });
      }
    },

    async handleFloatComplete() {
      this.showLoader = true;
      this.loaderMessage = 'Redirecting to POS...';
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      this.$router.push({ name: 'pos' });
    },

    async closeApplication() {
      if (!this.canCloseApp) return;
      const shouldClose = window.confirm('Close Lyt-ning POS?');
      if (!shouldClose) return;
      await window.electron.closeApp();
    },

    /**
     * Report this till's version to the store register and capture whether
     * the till is behind the store's latest version. Drives the blocking
     * update gate on the login screen (LNE-83).
     *
     * Called on every login-screen mount when a store is bound — the gate
     * depends on a fresh check-in each visit (a stale cached result would
     * let a refreshed till bypass the gate until the cache expired). The
     * check is one tiny POST; load is negligible. Failures are swallowed
     * (login must never break on a missing or unreachable register —
     * fail-open by design).
     */
    async runDeviceCheckIn() {
      if (!this.deviceName || !this.appVersion) return;
      const store = this.$store.getters.getDefaultStore;
      const storeDB = store && store.StoreDB;
      if (!storeDB) return;

      try {
        const result = await checkDeviceVersion(storeDB, {
          device: this.deviceName,
          app: getLocalAppName(),
          version: this.appVersion
        });
        this.deviceUpdate = result;
      } catch (e) {
        // Soft-fail (fail-open): offline / register unreachable must not lock
        // every store out. The gate stays hidden; the cashier can sign in.
        // eslint-disable-next-line no-console
        console.warn('[UnifiedLoginView] device check-in failed:', e && e.message);
      }
    },

    /**
     * Manually re-run the check-in (e.g. after support says "the update is
     * complete, please restart"). Re-evaluates the gate.
     */
    async gateRetry() {
      if (this.checking) return;
      this.checking = true;
      try {
        await this.runDeviceCheckIn();
      } finally {
        this.checking = false;
      }
    },

    openRequestUpdate() {
      this.showRequestModal = true;
    },

    closeRequestModal() {
      this.showRequestModal = false;
    },

    onRequestSubmitted() {
      this.updateRequestSent = true;
      // Match the existing TicketRequest success toast pattern (window.responses).
      if (typeof window !== 'undefined' && window.responses && window.responses.showInfo) {
        window.responses.showInfo('Update request sent — support will contact you');
      }
    },

    handleError(message) {
      if (this.$utils && this.$utils.showError) {
        this.$utils.showError(message);
      } else {
        alert(message);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.unified-login-page {
  background: var(--bg-gradient);
  min-height: 100vh;
  transition: var(--transition-speed);
  display: flex;
  flex-direction: column;
  position: relative;
}

.banner {
  height: 30vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: var(--text-primary);
  background: transparent;
  margin-top: -2px !important;
  max-height: 280px;
  position: relative;
  overflow: hidden;
  padding: 2rem 0;
}

.logo-container {
  text-align: center;
  opacity: 1;
  transform: translateY(0);
}

.lightning-logo {
  font-size: 3.5rem;
  font-weight: 700;
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple), var(--accent-electric-blue));
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  opacity: 1;
  transform: translateY(0);
}

.lightning-logo::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 3px;
  bottom: -5px;
  left: 0;
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple));
  transform: scaleX(1);
  transform-origin: bottom right;
}

.top-actions {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.close-app-btn {
  background: var(--danger);  /* theme token: replaces hardcoded #d9534f to align with --danger token — TicketApp #26670 */
  color: var(--text-on-accent);  /* theme token: replaces hardcoded #fff on red close button — TicketApp #26670 */
  cursor: pointer;
  min-height: 44px;
  padding: 0 1rem;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  font-weight: 600;
  box-shadow: var(--card-shadow);
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--card-shadow-hover);
    filter: brightness(1.03);
  }

  &:active {
    transform: translateY(0);
  }
}

.theme-toggle-btn {
  background: var(--card-bg);
  color: var(--text-primary);
  font-size: 1.2rem;
  cursor: pointer;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--card-shadow);
  border: var(--card-border);

  &:hover {
    box-shadow: var(--card-shadow-hover);
    color: var(--accent-electric-blue);
  }

  &:active {
  }
}

.login-container {
  width: 100%;
  max-width: 500px;
  margin-top: -40px;
  position: relative;
  min-height: 400px;
}

.loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 16px;
}

.loader-content {
  text-align: center;
  padding: 1.5rem;
  background-color: var(--card-bg);
  border-radius: 12px;
  box-shadow: var(--card-shadow);
  border: var(--card-border);
}

.spinner {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 3px solid rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.2);
  border-top-color: var(--accent-electric-blue);
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

.loader-message {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.footer-links {
  margin-top: auto;
  padding: 2rem 0;
  color: var(--text-secondary);
  opacity: 1;
  transform: translateY(0);
}

.version-label {
  font-size: 0.8rem;
  opacity: 0.75;
  margin-bottom: 0;
}

.device-id-label {
  word-break: break-all;
  user-select: all;
  max-width: 90vw;
  margin-left: auto;
  margin-right: auto;
}

.branded-link, .support-link {
  color: var(--accent-electric-blue);
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  font-weight: 500;

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 2px;
    bottom: -2px;
    left: 0;
    background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple));
    transform: scaleX(0);
    transform-origin: bottom right;
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &:hover {
    color: var(--accent-neon-purple);

    &::after {
      transform: scaleX(1);
      transform-origin: bottom left;
    }
  }
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-fade-enter-from {
  transform: translateX(20px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

/* Animations removed for compatibility with reduced motion settings */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Dark mode adjustments for loader */
[data-theme='dark'] .loader-overlay {
  background: rgba(0, 0, 0, 0.7);
}

/* Responsive */
@media (max-width: 768px) {
  .top-actions {
    top: 16px;
    right: 16px;
    gap: 0.5rem;
  }

  .close-app-btn {
    min-height: 40px;
    padding: 0 0.85rem;
    font-size: 0.9rem;
  }

  .login-container {
    max-width: 95vw;
    margin-top: -20px;
  }

  .lightning-logo {
    font-size: 2.5rem;
  }

  .banner {
    height: 25vh;
  }

  .loader-content {
    padding: 1.25rem;
    margin: 0 1rem;
  }

  .spinner {
    width: 28px;
    height: 28px;
    border-width: 2.5px;
  }

  .loader-message {
    font-size: 0.9rem;
  }
}

/* Update gate — full-screen blocking overlay on the login screen only (LNE-83) */
.update-gate {
  position: fixed;
  inset: 0;
  z-index: 1040;
  background: var(--bg-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}

.update-gate-card {
  width: 100%;
  max-width: 480px;
  background: var(--card-bg);
  color: var(--text-primary);
  border: 2px solid var(--danger, #d9534f);
  border-radius: 14px;
  box-shadow: var(--card-shadow-hover, 0 12px 32px rgba(0, 0, 0, 0.25));
  padding: 2rem 1.75rem;
  text-align: center;
}

.update-gate-icon {
  color: var(--danger, #d9534f);
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
}

.update-gate-title {
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 0.75rem;
  color: var(--text-primary);
  line-height: 1.3;
}

.update-gate-detail {
  font-size: 0.95rem;
  color: var(--text-primary);
  margin: 0 0 1rem;
  line-height: 1.45;
}

.update-gate-fineprint {
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin: 0 0 1.5rem;
  line-height: 1.4;
}

.update-gate-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.update-gate-actions .btn {
  min-height: 44px;
  min-width: 200px;
  padding: 0 1.5rem;
  font-weight: 600;
  border-radius: 8px;
}

.update-gate-actions .btn-primary {
  background: var(--accent-electric-blue, #339cff);
  border: 1px solid var(--accent-electric-blue, #339cff);
  color: var(--text-on-accent, #fff);
}

.update-gate-actions .btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.update-gate-actions .btn-link.check-again-btn {
  background: transparent;
  border: none;
  color: var(--accent-electric-blue, #339cff);
  text-decoration: underline;
  min-height: 36px;
  font-size: 0.9rem;
}

.update-gate-actions .btn-link.check-again-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.update-gate-sent {
  margin: 1rem 0 0;
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.4;
}

@media (max-width: 768px) {
  .update-gate {
    padding: 1rem;
  }
  .update-gate-card {
    padding: 1.5rem 1.25rem;
  }
  .update-gate-title {
    font-size: 1.1rem;
  }
}
</style>
